<template>
  <VModal :showing="showing" container-classes="w-192" data-testid="deck-slide-selector-root" @update:showing="$emit('close')">
    <template #header>
      <VModalHeader>
        <template #title>Choose any number of slides from {{ selectedDeck.title }}</template>
        <template #sub-title>Import slides from another 3D Presentation</template>
      </VModalHeader>
    </template>

    <template #default>
      <div v-if="hasNoSlides" class="text-gray-600 font-light text-center max-w-xs mx-auto mt-10 mb-16" data-testid="empty-message">
        <p class="text-xl mb-4">This 3D Presentation has no 3D slides.</p>
        <p class="mb-0">Please contact your VRIFY support team to track the completion of your Project</p>
      </div>

      <VSearchableThumbnailList
        v-else
        ref="thumbnailListRef"
        v-model:search="search"
        :auto-load="true"
        :options="slideList as unknown as Option[]"
        :loading="loading"
        :count="count"
        placeholder="Search slides"
        data-testid="thumbnail-list"
        @selected="toggleSelected($event.id)"
        @load-more="scroll()"
      >
        <template #item="{ option }">
          <EditorDeckThumbnailTile
            readonly
            variant="search"
            :item="option as unknown as DeepReadonly<Slide>"
            :selected="isItemSelected(option)"
            placeholder="Slide Name"
            :data-testid="`tile-${option.id}`"
            class="slide"
          >
            <template #default>
              <img
                v-if="getSlideImage(option as unknown as Slide)"
                :src="getSlideImage(option as unknown as Slide) as string"
                draggable="false"
                class="h-full w-full object-cover"
              />
              <UrlSlideThumbnail v-else-if="(option as unknown as Slide).type === 'D'" :slide="option as unknown as Slide" />
              <HeaderSlideThumbnail v-else-if="(option as unknown as Slide).type === 'H'" />
              <PlaceholderDeck v-else />
            </template>
          </EditorDeckThumbnailTile>
        </template>
      </VSearchableThumbnailList>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton @click="$emit('back')">Back</VButton>
        <VButton
          class="w-48"
          variant="copper"
          data-testid="copy-slides"
          :disabled="!selectedItems.length || isCopying"
          @click="copySelectedSlides"
        >
          Import {{ `${selectedItems.length || ''} ${pluralize(selectedItems.length, 'Slide', 'Slides')}` }}
        </VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, computed } from 'vue'

import type { Model3D } from '@/entities/model3d'
import type { Deck, Slide } from '@/entities/deck'
import type { QueryParameters } from '@/composables/useIndexSearch'
import type { MultiSelectItem } from '@/composables/useMultipleSlidesSelect'
import type { Option } from '@/components/VSearchableThumbnailList.vue'
import type { EditDeck } from '@/composables/useEditDeck'
import { pluralize } from '@/lib/pluralize'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'
import { isThreeDSlide } from '@/lib/type-utils'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'

interface ModelQuery extends QueryParameters {
  sortKey: 'name' | 'updated_at'
  sortDir: '-' | ''
}
interface ApiQuery {
  query: string | null
  limit: number
}

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  selectedDeck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
})

const emit = defineEmits(['back', 'close', 'copy-slides'])

const thumbnailListRef = ref<any | undefined>()
const slideList = computed(() => results.value ?? [])
const selected = ref(false)
const isCopying = ref(false)
const hasNoSlides = computed(() => !loading.value && !search.value?.trim() && !results.value.length)

const {
  results,
  query: search,
  count,
  continuation,
  loading,
} = useIndexSearch<Slide, ModelQuery, ApiQuery>({
  initParams: { limit: 12, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => `v2/decks/${props.selectedDeck.id}/slides`,
  finalParameters: ({ query, limit }) => ({
    query,
    limit,
    include: ['model_camera', 'site_image'],
  }),
})

const { selectedSlideIds: selectedItems, onClick: toggleSelected } = useMultipleSlidesSelect({
  slides: computed(() => slideList.value.map(x => ({ id: x.id })) as readonly MultiSelectItem[]),
  currentSlideId: computed(() => Infinity),
  setCurrentSlideIndex: () => {},
  suppressReset: true,
})

function isItemSelected(item: Option): boolean {
  return selectedItems.value.some(id => id === item.id)
}

function copySelectedSlides() {
  // To keep the order in which the slides were selected
  const newSlides = selectedItems.value.reduce((acc: Slide[], val: number) => {
    const item = slideList.value.find(x => x.id === val)
    return !item ? acc : acc.concat(item)
  }, [])

  emit('copy-slides', newSlides)
  isCopying.value = true
}

async function scroll() {
  if (!loading.value && count.value > results.value.length) {
    continuation.value++
  }
}

function getSlideImage(slide: Slide) {
  if (slide.type === 'S') return slide.site_image
  if (isThreeDSlide(slide)) return slide.model_camera?.preview

  return (
    (slide.image?.id && slide.image?.url) || // High res version of the processed image of the PDF
    slide.preview?.url || // Low res version of the processed image of the PDF
    slide.file?.thumbnail || // generated PDF thumbnail
    slide.file?.url // PDF itself
  )
}
</script>
